import { CompleteKundali, PlanetPosition, ZodiacSign, Language } from '../types/kundali';
import { calculateLivePlanetaryEphemeris } from './vedicEngine';

// ============================================================================
// CONSTANTS & REFERENCE TABLES
// ============================================================================

export const ZODIAC_SIGNS_LIST: ZodiacSign[] = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

export const SIGN_LORDS: Record<ZodiacSign, string> = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter'
};

export const SIGN_ELEMENTS: Record<ZodiacSign, { en: string; hi: string; color: string }> = {
  Aries: { en: 'Fire', hi: 'अग्नि', color: 'text-amber-600 bg-amber-500/10 border-amber-500/30' },
  Taurus: { en: 'Earth', hi: 'पृथ्वी', color: 'text-emerald-700 bg-emerald-500/10 border-emerald-500/30' },
  Gemini: { en: 'Air', hi: 'वायु', color: 'text-sky-600 bg-sky-500/10 border-sky-500/30' },
  Cancer: { en: 'Water', hi: 'जल', color: 'text-blue-600 bg-blue-500/10 border-blue-500/30' },
  Leo: { en: 'Fire', hi: 'अग्नि', color: 'text-amber-600 bg-amber-500/10 border-amber-500/30' },
  Virgo: { en: 'Earth', hi: 'पृथ्वी', color: 'text-emerald-700 bg-emerald-500/10 border-emerald-500/30' },
  Libra: { en: 'Air', hi: 'वायु', color: 'text-sky-600 bg-sky-500/10 border-sky-500/30' },
  Scorpio: { en: 'Water', hi: 'जल', color: 'text-blue-600 bg-blue-500/10 border-blue-500/30' },
  Sagittarius: { en: 'Fire', hi: 'अग्नि', color: 'text-amber-600 bg-amber-500/10 border-amber-500/30' },
  Capricorn: { en: 'Earth', hi: 'पृथ्वी', color: 'text-emerald-700 bg-emerald-500/10 border-emerald-500/30' },
  Aquarius: { en: 'Air', hi: 'वायु', color: 'text-sky-600 bg-sky-500/10 border-sky-500/30' },
  Pisces: { en: 'Water', hi: 'जल', color: 'text-blue-600 bg-blue-500/10 border-blue-500/30' }
};

export const SIGN_QUALITIES: Record<ZodiacSign, { en: string; hi: string; type: 'movable' | 'fixed' | 'dual' }> = {
  Aries: { en: 'Movable (Chara)', hi: 'चर (गतिशील)', type: 'movable' },
  Taurus: { en: 'Fixed (Sthira)', hi: 'स्थिर (दृढ़)', type: 'fixed' },
  Gemini: { en: 'Dual (Dvisvabhava)', hi: 'द्विस्वभाव (लचीला)', type: 'dual' },
  Cancer: { en: 'Movable (Chara)', hi: 'चर (गतिशील)', type: 'movable' },
  Leo: { en: 'Fixed (Sthira)', hi: 'स्थिर (दृढ़)', type: 'fixed' },
  Virgo: { en: 'Dual (Dvisvabhava)', hi: 'द्विस्वभाव (लचीला)', type: 'dual' },
  Libra: { en: 'Movable (Chara)', hi: 'चर (गतिशील)', type: 'movable' },
  Scorpio: { en: 'Fixed (Sthira)', hi: 'स्थिर (दृढ़)', type: 'fixed' },
  Sagittarius: { en: 'Dual (Dvisvabhava)', hi: 'द्विस्वभाव (लचीला)', type: 'dual' },
  Capricorn: { en: 'Movable (Chara)', hi: 'चर (गतिशील)', type: 'movable' },
  Aquarius: { en: 'Fixed (Sthira)', hi: 'स्थिर (दृढ़)', type: 'fixed' },
  Pisces: { en: 'Dual (Dvisvabhava)', hi: 'द्विस्वभाव (लचीला)', type: 'dual' }
};

// Parashari Kala Rays for Indu Lagna
export const PLANET_KALA_RAYS: Record<string, number> = {
  Sun: 30,
  Moon: 16,
  Mars: 6,
  Mercury: 8,
  Jupiter: 10,
  Venus: 12,
  Saturn: 1
};

// Classical Exaltation and Debilitation Signs
export const EXALTATION_SIGNS: Record<string, ZodiacSign> = {
  Sun: 'Aries',
  Moon: 'Taurus',
  Mars: 'Capricorn',
  Mercury: 'Virgo',
  Jupiter: 'Cancer',
  Venus: 'Pisces',
  Saturn: 'Libra',
  Rahu: 'Taurus',
  Ketu: 'Scorpio'
};

export const DEBILITATION_SIGNS: Record<string, ZodiacSign> = {
  Sun: 'Libra',
  Moon: 'Scorpio',
  Mars: 'Cancer',
  Mercury: 'Pisces',
  Jupiter: 'Capricorn',
  Venus: 'Virgo',
  Saturn: 'Aries',
  Rahu: 'Scorpio',
  Ketu: 'Taurus'
};

// Classical Mrityu Bhaga (Fatal Degrees) per Rashi
export const MRITYU_BHAGA_DEGREES: Record<string, number[]> = {
  Sun: [20, 9, 12, 6, 8, 24, 16, 17, 22, 2, 3, 23],
  Moon: [26, 12, 13, 25, 24, 11, 26, 14, 13, 25, 5, 12],
  Mars: [19, 28, 23, 29, 2, 8, 24, 13, 23, 15, 11, 17],
  Mercury: [15, 14, 13, 12, 8, 18, 20, 10, 21, 22, 7, 5],
  Jupiter: [19, 29, 12, 27, 6, 4, 13, 10, 17, 11, 15, 28],
  Venus: [28, 15, 11, 17, 10, 13, 4, 6, 27, 12, 29, 19],
  Saturn: [10, 4, 7, 9, 12, 16, 3, 18, 28, 14, 13, 15],
  Rahu: [14, 13, 12, 11, 24, 23, 24, 11, 10, 8, 18, 20],
  Ketu: [8, 18, 20, 10, 21, 22, 7, 5, 14, 13, 12, 11]
};

// Pushkara Bhaga degrees (auspicious degrees in each sign)
export const PUSHKARA_BHAGA_DEGREES: number[] = [
  21, // Aries
  14, // Taurus
  18, // Gemini
  8,  // Cancer
  19, // Leo
  9,  // Virgo
  24, // Libra
  11, // Scorpio
  23, // Sagittarius
  14, // Capricorn
  19, // Aquarius
  9   // Pisces
];

// ============================================================================
// INTERFACES & RETURN TYPES
// ============================================================================

export interface ThreePillarsSummary {
  lagna: {
    sign: ZodiacSign;
    degree: number;
    degreeFormatted: string;
    lord: string;
    element: { en: string; hi: string; color: string };
    quality: { en: string; hi: string; type: string };
    nakshatra: string;
    pada: number;
    houseLordshipVerdict: string;
  };
  chandra: {
    sign: ZodiacSign;
    degree: number;
    degreeFormatted: string;
    lord: string;
    element: { en: string; hi: string; color: string };
    nakshatra: string;
    pada: number;
    paksha: string;
    mindVerdict: string;
  };
  surya: {
    sign: ZodiacSign;
    degree: number;
    degreeFormatted: string;
    lord: string;
    element: { en: string; hi: string; color: string };
    nakshatra: string;
    pada: number;
    soulVerdict: string;
  };
}

export interface FunctionalPlanetaryClassification {
  planet: string;
  role: 'yogakaraka' | 'benefic' | 'malefic' | 'maraka' | 'neutral';
  isBadhakesh: boolean;
  housesOwned: number[];
  currentSign: ZodiacSign;
  currentHouse: number;
  dignity: string;
  summary: string;
}

export interface VulnerabilityAlert {
  planet: string;
  type: 'gandanta' | 'mrityu_bhaga' | 'combustion' | 'planetary_war' | 'sandhi';
  severity: 'high' | 'medium' | 'critical';
  titleEn: string;
  titleHi: string;
  descriptionEn: string;
  descriptionHi: string;
}

export interface AuspiciousAlert {
  planet: string;
  type: 'vargottama' | 'pushkara_navamsha' | 'pushkara_bhaga' | 'exalted';
  titleEn: string;
  titleHi: string;
  descriptionEn: string;
  descriptionHi: string;
}

export interface NeechaBhangaResult {
  planet: string;
  debilitatedSign: ZodiacSign;
  house: number;
  isCancelled: boolean;
  score: number; // 0 to 4
  conditionsMet: string[];
  verdictEn: string;
  verdictHi: string;
}

export interface JaiminiKarakas {
  atmakaraka: { planet: string; degree: number; sign: ZodiacSign; meaning: string };
  amatyakaraka: { planet: string; degree: number; sign: ZodiacSign; meaning: string };
  bhratrikaraka: { planet: string; degree: number; sign: ZodiacSign; meaning: string };
  matrikaraka: { planet: string; degree: number; sign: ZodiacSign; meaning: string };
  putrakaraka: { planet: string; degree: number; sign: ZodiacSign; meaning: string };
  gnatikaraka: { planet: string; degree: number; sign: ZodiacSign; meaning: string };
  darakaraka: { planet: string; degree: number; sign: ZodiacSign; meaning: string };
  karakamshaSign: ZodiacSign;
  karakamshaHouse: number;
  spiritualFocusEn: string;
  spiritualFocusHi: string;
}

export interface KartariStatus {
  target: string;
  type: 'papa_kartari' | 'shubha_kartari' | 'neutral';
  twelfthPlanets: string[];
  secondPlanets: string[];
  verdictEn: string;
  verdictHi: string;
}

export interface D1toD9ShiftItem {
  planet: string;
  d1Sign: ZodiacSign;
  d1House: number;
  d1Dignity: string;
  d9Sign: ZodiacSign;
  d9House: number;
  d9Dignity: string;
  shiftVerdict: 'vargottama' | 'exalted_to_debilitated' | 'debilitated_to_exalted' | 'improved' | 'weakened' | 'steady';
  interpretationEn: string;
  interpretationHi: string;
}

export interface SpecialLagnasSummary {
  induLagna: {
    sign: ZodiacSign;
    houseFromLagna: number;
    occupants: string[];
    aspectedBy: string[];
    wealthCeilingScore: number; // 1 to 10
    verdictEn: string;
    verdictHi: string;
  };
  upapadaLagna: {
    sign: ZodiacSign;
    houseFromLagna: number;
    verdictEn: string;
    verdictHi: string;
  };
}

export interface DoubleTransitItem {
  houseNumber: number;
  sign: ZodiacSign;
  significanceEn: string;
  significanceHi: string;
  saturnInfluence: string; // 'occupies' | 'aspects_3' | 'aspects_7' | 'aspects_10'
  jupiterInfluence: string; // 'occupies' | 'aspects_5' | 'aspects_7' | 'aspects_9'
  isSimultaneouslyActivated: boolean;
  activatedLifeDomain: string;
  practicalTimingVerdictEn: string;
  practicalTimingVerdictHi: string;
}

export interface AstrologerDiagnosticReport {
  timestamp: string;
  threePillars: ThreePillarsSummary;
  functionalClassification: FunctionalPlanetaryClassification[];
  kendraTrikonaScore: {
    kendraBenefics: number;
    kendraMalefics: number;
    trikonaBenefics: number;
    dusthanaAfflictionScore: number;
    overallPillarStrength: 'exceptional' | 'solid' | 'mixed' | 'challenging';
    summaryEn: string;
    summaryHi: string;
  };
  vulnerabilities: VulnerabilityAlert[];
  auspiciousMarks: AuspiciousAlert[];
  neechaBhanga: NeechaBhangaResult[];
  jaiminiKarakas: JaiminiKarakas;
  kartari: KartariStatus[];
  d1toD9Shifts: D1toD9ShiftItem[];
  specialLagnas: SpecialLagnasSummary;
  doubleTransitRadar: DoubleTransitItem[];
  activeDashaResonance: {
    mahadasha: string;
    antardasha: string;
    alignmentWithDoubleTransit: boolean;
    operationalAdviceEn: string;
    operationalAdviceHi: string;
  };
  karmaDiagnostic: {
    type: 'dridha' | 'dridha_adridha' | 'adridha';
    titleEn: string;
    titleHi: string;
    prescriptionEn: string;
    prescriptionHi: string;
  };
  consultationBrief: {
    first60SecondsTakeawayEn: string;
    first60SecondsTakeawayHi: string;
    criticalWarningEn?: string;
    criticalWarningHi?: string;
    primaryBlessingEn: string;
    primaryBlessingHi: string;
  };
}

// ============================================================================
// COMPUTATION ENGINE
// ============================================================================

export function generateAstrologerDiagnosticDossier(kundali: CompleteKundali): AstrologerDiagnosticReport {
  const d1Planets = kundali.planets || [];
  const d1Houses = kundali.houses || [];
  const lagnaHouse = d1Houses.find(h => h.houseNumber === 1) || d1Houses[0];
  const lagnaSign: ZodiacSign = lagnaHouse?.sign || 'Aries';
  const lagnaSignIdx = ZODIAC_SIGNS_LIST.indexOf(lagnaSign);

  const d9Planets = (kundali.charts?.D9?.planets as PlanetPosition[]) || [];

  // Helper to find planet
  const getP = (name: string): PlanetPosition | undefined => d1Planets.find(p => p.name.toLowerCase() === name.toLowerCase());
  const getD9P = (name: string): PlanetPosition | undefined => d9Planets.find(p => p.name.toLowerCase() === name.toLowerCase());

  const lagnaP = getP('Ascendant');
  const moonP = getP('Moon');
  const sunP = getP('Sun');
  const marsP = getP('Mars');
  const mercP = getP('Mercury');
  const jupP = getP('Jupiter');
  const venP = getP('Venus');
  const satP = getP('Saturn');
  const rahuP = getP('Rahu');
  const ketuP = getP('Ketu');

  // --------------------------------------------------------------------------
  // 1. PHASE 1: THREE PILLARS (Lagna, Moon, Sun)
  // --------------------------------------------------------------------------
  const lagnaElement = SIGN_ELEMENTS[lagnaSign];
  const lagnaQuality = SIGN_QUALITIES[lagnaSign];
  const lagnaLord = SIGN_LORDS[lagnaSign];

  const moonSign = moonP?.sign || 'Taurus';
  const moonElement = SIGN_ELEMENTS[moonSign];

  const sunSign = sunP?.sign || 'Leo';
  const sunElement = SIGN_ELEMENTS[sunSign];

  // Paksha estimation from Moon-Sun distance
  let paksha = 'Shukla Paksha (Waxing Bright)';
  if (moonP && sunP) {
    const diff = (moonP.longitude - sunP.longitude + 360) % 360;
    paksha = diff < 180 ? 'Shukla Paksha (Bright Fortnight)' : 'Krishna Paksha (Dark Fortnight)';
  }

  const threePillars: ThreePillarsSummary = {
    lagna: {
      sign: lagnaSign,
      degree: lagnaP?.degreeInSign || 15,
      degreeFormatted: lagnaP?.degreeFormatted || `${Math.round(lagnaP?.degreeInSign || 15)}° 00'`,
      lord: lagnaLord,
      element: lagnaElement,
      quality: lagnaQuality,
      nakshatra: lagnaP?.nakshatra || 'Ashwini',
      pada: lagnaP?.pada || 1,
      houseLordshipVerdict: `Governs physical vessel, self-image, and longevity. Lord ${lagnaLord} positioned in House ${getP(lagnaLord)?.house || 1}.`
    },
    chandra: {
      sign: moonSign,
      degree: moonP?.degreeInSign || 15,
      degreeFormatted: moonP?.degreeFormatted || '15° 00\'',
      lord: SIGN_LORDS[moonSign],
      element: moonElement,
      nakshatra: moonP?.nakshatra || 'Rohini',
      pada: moonP?.pada || 1,
      paksha,
      mindVerdict: `Perception lens & emotional resilience. Moon placed in ${moonSign} (${moonElement.en} element) reflects ${moonElement.en === 'Water' ? 'deep intuition & receptivity' : moonElement.en === 'Fire' ? 'passionate, reactive instincts' : moonElement.en === 'Earth' ? 'pragmatic, grounded stability' : 'intellectual, communicative nature'}.`
    },
    surya: {
      sign: sunSign,
      degree: sunP?.degreeInSign || 15,
      degreeFormatted: sunP?.degreeFormatted || '15° 00\'',
      lord: SIGN_LORDS[sunSign],
      element: sunElement,
      nakshatra: sunP?.nakshatra || 'Krittika',
      pada: sunP?.pada || 1,
      soulVerdict: `Atma / Soul vigor & authority. Sun in ${sunSign} in House ${sunP?.house || 1} drives societal rank and core willpower.`
    }
  };

  // --------------------------------------------------------------------------
  // 2. FUNCTIONAL CLASSIFICATION (Yogakaraka, Benefics, Malefics, Badhakesh)
  // --------------------------------------------------------------------------
  // Badhaka house rule: Movable=11, Fixed=9, Dual=7
  const badhakaHouseNum = lagnaQuality.type === 'movable' ? 11 : lagnaQuality.type === 'fixed' ? 9 : 7;
  const badhakaSign = ZODIAC_SIGNS_LIST[(lagnaSignIdx + badhakaHouseNum - 1) % 12];
  const badhakeshLord = SIGN_LORDS[badhakaSign];

  // Specific Functional maps per Ascendant
  interface LagnaRule {
    yogakaraka?: string;
    benefics: string[];
    malefics: string[];
    marakas: string[];
    neutrals: string[];
  }

  const LAGNA_RULES: Record<ZodiacSign, LagnaRule> = {
    Aries: {
      benefics: ['Sun', 'Jupiter', 'Mars'],
      malefics: ['Mercury', 'Saturn'],
      marakas: ['Venus'],
      neutrals: ['Moon']
    },
    Taurus: {
      yogakaraka: 'Saturn',
      benefics: ['Saturn', 'Mercury', 'Venus'],
      malefics: ['Jupiter', 'Moon'],
      marakas: ['Mars', 'Jupiter'],
      neutrals: ['Sun']
    },
    Gemini: {
      benefics: ['Venus', 'Saturn'],
      malefics: ['Mars', 'Sun'],
      marakas: ['Jupiter', 'Mars'],
      neutrals: ['Mercury', 'Moon']
    },
    Cancer: {
      yogakaraka: 'Mars',
      benefics: ['Mars', 'Jupiter', 'Moon'],
      malefics: ['Venus', 'Mercury'],
      marakas: ['Saturn', 'Sun'],
      neutrals: ['Sun']
    },
    Leo: {
      yogakaraka: 'Mars',
      benefics: ['Mars', 'Sun', 'Jupiter'],
      malefics: ['Venus', 'Saturn'],
      marakas: ['Saturn', 'Mercury'],
      neutrals: ['Moon']
    },
    Virgo: {
      benefics: ['Venus', 'Mercury'],
      malefics: ['Mars', 'Moon'],
      marakas: ['Jupiter', 'Mars'],
      neutrals: ['Sun', 'Saturn']
    },
    Libra: {
      yogakaraka: 'Saturn',
      benefics: ['Saturn', 'Mercury', 'Venus'],
      malefics: ['Jupiter', 'Sun'],
      marakas: ['Mars'],
      neutrals: ['Moon']
    },
    Scorpio: {
      benefics: ['Sun', 'Moon', 'Jupiter', 'Mars'],
      malefics: ['Mercury', 'Venus'],
      marakas: ['Venus'],
      neutrals: ['Saturn']
    },
    Sagittarius: {
      benefics: ['Sun', 'Mars', 'Jupiter'],
      malefics: ['Venus'],
      marakas: ['Mercury', 'Saturn'],
      neutrals: ['Moon']
    },
    Capricorn: {
      yogakaraka: 'Venus',
      benefics: ['Venus', 'Mercury', 'Saturn'],
      malefics: ['Mars', 'Jupiter', 'Moon'],
      marakas: ['Moon', 'Jupiter'],
      neutrals: ['Sun']
    },
    Aquarius: {
      yogakaraka: 'Venus',
      benefics: ['Venus', 'Saturn', 'Mercury'],
      malefics: ['Jupiter', 'Moon', 'Mars'],
      marakas: ['Sun', 'Jupiter'],
      neutrals: ['Mars']
    },
    Pisces: {
      benefics: ['Moon', 'Mars', 'Jupiter'],
      malefics: ['Venus', 'Sun'],
      marakas: ['Mercury', 'Saturn'],
      neutrals: ['Saturn']
    }
  };

  const currentLagnaRule = LAGNA_RULES[lagnaSign] || LAGNA_RULES.Aries;

  const functionalClassification: FunctionalPlanetaryClassification[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].map(pName => {
    const pData = getP(pName);
    const isYogakaraka = currentLagnaRule.yogakaraka === pName;
    const isBadhakesh = badhakeshLord.toLowerCase() === pName.toLowerCase();
    
    let role: 'yogakaraka' | 'benefic' | 'malefic' | 'maraka' | 'neutral' = 'neutral';
    if (isYogakaraka) role = 'yogakaraka';
    else if (currentLagnaRule.benefics.includes(pName)) role = 'benefic';
    else if (currentLagnaRule.malefics.includes(pName)) role = 'malefic';
    else if (currentLagnaRule.marakas.includes(pName)) role = 'maraka';

    // Find houses owned by this planet
    const housesOwned: number[] = [];
    d1Houses.forEach(h => {
      if (h.signLord.toLowerCase() === pName.toLowerCase()) {
        housesOwned.push(h.houseNumber);
      }
    });

    let summary = '';
    if (isYogakaraka) {
      summary = `Supreme Yogakaraka for ${lagnaSign} Lagna (Lord of Kendra & Trikona houses ${housesOwned.join(' & ')}). Brings auspicious expansion when unafflicted.`;
    } else if (isBadhakesh) {
      summary = `Badhakesh (Lord of ${badhakaHouseNum}th house ${badhakaSign}). Creates unexpected delays or stubborn impediments during its Dasha/transit.`;
    } else if (role === 'benefic') {
      summary = `Functional Benefic governing supportive houses (${housesOwned.join(', ')}).`;
    } else if (role === 'maraka') {
      summary = `Maraka planet governing transformation/physical vulnerability (House ${housesOwned.join(' & ')}).`;
    } else {
      summary = `Functional Malefic / Obstacle ruler (House ${housesOwned.join(' & ')}). Needs pacification rather than gemstone activation.`;
    }

    return {
      planet: pName,
      role,
      isBadhakesh,
      housesOwned,
      currentSign: pData?.sign || 'Aries',
      currentHouse: pData?.house || 1,
      dignity: pData?.dignity || 'Neutral',
      summary
    };
  });

  // --------------------------------------------------------------------------
  // 3. KENDRA & TRIKONA HEALTH SCORE
  // --------------------------------------------------------------------------
  let kendraBenefics = 0;
  let kendraMalefics = 0;
  let trikonaBenefics = 0;
  let dusthanaAfflictions = 0;

  const naturalBenefics = ['jupiter', 'venus', 'mercury', 'moon'];
  const naturalMalefics = ['saturn', 'mars', 'rahu', 'ketu', 'sun'];

  d1Planets.forEach(p => {
    const isNatBen = naturalBenefics.includes(p.name.toLowerCase());
    const isNatMal = naturalMalefics.includes(p.name.toLowerCase());

    if ([1, 4, 7, 10].includes(p.house)) {
      if (isNatBen) kendraBenefics++;
      if (isNatMal) kendraMalefics++;
    }
    if ([5, 9].includes(p.house)) {
      if (isNatBen) trikonaBenefics++;
    }
    if ([6, 8, 12].includes(p.house)) {
      if (isNatMal) dusthanaAfflictions++;
    }
  });

  let pillarStrength: 'exceptional' | 'solid' | 'mixed' | 'challenging' = 'solid';
  if (kendraBenefics >= 2 && kendraMalefics <= 1) pillarStrength = 'exceptional';
  else if (kendraMalefics > kendraBenefics && dusthanaAfflictions >= 2) pillarStrength = 'challenging';
  else if (kendraBenefics >= 1) pillarStrength = 'solid';
  else pillarStrength = 'mixed';

  // --------------------------------------------------------------------------
  // 4. VULNERABILITIES & AUSPICIOUS ALERTS (Gandanta, Mrityu Bhaga, Pushkara)
  // --------------------------------------------------------------------------
  const vulnerabilities: VulnerabilityAlert[] = [];
  const auspiciousMarks: AuspiciousAlert[] = [];

  // Check Gandanta (Water to Fire transition junctions)
  // Revati (Pisces) to Ashwini (Aries): 26°40' Pisces - 3°20' Aries
  // Ashlesha (Cancer) to Magha (Leo): 26°40' Cancer - 3°20' Leo
  // Jyeshtha (Scorpio) to Mula (Sagittarius): 26°40' Scorpio - 3°20' Sagittarius
  const GANDANTA_RASHIS: Record<ZodiacSign, 'water' | 'fire'> = {
    Pisces: 'water',
    Aries: 'fire',
    Cancer: 'water',
    Leo: 'fire',
    Scorpio: 'water',
    Sagittarius: 'fire',
    Taurus: 'water', Gemini: 'water', Virgo: 'water', Libra: 'water', Capricorn: 'water', Aquarius: 'water' // dummy
  };

  const testGandanta = (name: string, sign: ZodiacSign, deg: number) => {
    if (['Pisces', 'Cancer', 'Scorpio'].includes(sign) && deg >= 26.66) {
      vulnerabilities.push({
        planet: name,
        type: 'gandanta',
        severity: deg >= 29 ? 'critical' : 'high',
        titleEn: `${name} in Gandanta (Water-Fire Karmic Knot)`,
        titleHi: `${name} गंडांत में (जल-अग्नि संधि ग्रंथि)`,
        descriptionEn: `${name} is at ${deg.toFixed(2)}° of ${sign} (the junction of water and fire signs). Indicates past-life karmic knots and psychological sensitivity requiring spiritual remedies.`,
        descriptionHi: `${name} ${sign} के ${deg.toFixed(2)}° पर स्थित है। यह जल और अग्नि राशि की गंभीर संधि है, जो पूर्वजन्म के कर्म बंधनों को दर्शाती है।`
      });
    } else if (['Aries', 'Leo', 'Sagittarius'].includes(sign) && deg <= 3.33) {
      vulnerabilities.push({
        planet: name,
        type: 'gandanta',
        severity: deg <= 1 ? 'critical' : 'high',
        titleEn: `${name} in Gandanta Entry (Fire-Sign Origin)`,
        titleHi: `${name} गंडांत प्रवेश (अग्नि राशि आरंभ)`,
        descriptionEn: `${name} is at ${deg.toFixed(2)}° of ${sign} (beginning of nakshatras Ashwini, Magha, or Mula). Vulnerable to sudden structural shocks in health or family dynamics.`,
        descriptionHi: `${name} ${sign} के प्रारंभिक ${deg.toFixed(2)}° पर स्थित है। इस पर शांति कर्म व मंत्र साधना विशेष फलदायी होती है।`
      });
    }
  };

  if (lagnaP) testGandanta('Ascendant (Lagna)', lagnaSign, lagnaP.degreeInSign);
  d1Planets.forEach(p => {
    testGandanta(p.name, p.sign, p.degreeInSign);
  });

  // Check Mrityu Bhaga (Fatal Degrees)
  d1Planets.forEach(p => {
    const list = MRITYU_BHAGA_DEGREES[p.name];
    if (list) {
      const targetDeg = list[p.signIndex];
      if (targetDeg !== undefined) {
        const delta = Math.abs(p.degreeInSign - targetDeg);
        if (delta <= 1.2) {
          vulnerabilities.push({
            planet: p.name,
            type: 'mrityu_bhaga',
            severity: delta <= 0.5 ? 'critical' : 'high',
            titleEn: `${p.name} in Mrityu Bhaga (${targetDeg}° of ${p.sign})`,
            titleHi: `${p.name} मृत्यु भाग में (${p.sign} के ${targetDeg}° पर)`,
            descriptionEn: `${p.name} sits precisely at ${p.degreeInSign.toFixed(2)}° of ${p.sign}, matching its classical Mrityu Bhaga degree (${targetDeg}°). During its Dasha/Bhukti, health vulnerabilities or sharp reversals may manifest.`,
            descriptionHi: `${p.name} अपनी राशि के मृत्यु भाग (${targetDeg}°) के अत्यंत निकट (${p.degreeInSign.toFixed(2)}°) है। इसकी दशा में विशेष सावधानी की आवश्यकता होती है।`
          });
        }
      }
    }

    // Check Pushkara Bhaga (Auspicious Degrees)
    const pBhaga = PUSHKARA_BHAGA_DEGREES[p.signIndex];
    if (pBhaga !== undefined && Math.abs(p.degreeInSign - pBhaga) <= 1.5) {
      auspiciousMarks.push({
        planet: p.name,
        type: 'pushkara_bhaga',
        titleEn: `${p.name} in Sacred Pushkara Bhaga (${pBhaga}° of ${p.sign})`,
        titleHi: `${p.name} पवित्र पुष्कर भाग में (${p.sign} के ${pBhaga}° पर)`,
        descriptionEn: `${p.name} occupies an exact Pushkara degree (${p.degreeInSign.toFixed(2)}°). Bestows divine regeneration, recovery from hardship, and long-term protection.`,
        descriptionHi: `${p.name} अपनी राशि के अत्यंत पावन पुष्कर भाग में है। यह संकटों से उबारने और जातक को ईश्वरीय कृपा दिलाने में सक्षम है।`
      });
    }

    // Combustion
    if (p.isCombust && p.name !== 'Sun') {
      vulnerabilities.push({
        planet: p.name,
        type: 'combustion',
        severity: 'medium',
        titleEn: `${p.name} is Combust (Asta by Sun)`,
        titleHi: `${p.name} अस्त (सूर्य के निकट)`,
        descriptionEn: `${p.name} is close to the Sun, obscuring its outward physical manifestations while intensifying inward psychological awareness.`,
        descriptionHi: `${p.name} सूर्य के प्रभाव से अस्त है। इसके भौतिक फल में अवरोध किंतु आध्यात्मिक अंतर्दृष्टि में वृद्धि होती है।`
      });
    }
  });

  // --------------------------------------------------------------------------
  // 5. DISPOSITOR & NEECHA BHANGA RAJA YOGA (NBRY)
  // --------------------------------------------------------------------------
  const neechaBhanga: NeechaBhangaResult[] = [];

  ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].forEach(pName => {
    const pData = getP(pName);
    if (!pData) return;

    const debSign = DEBILITATION_SIGNS[pName];
    if (pData.sign === debSign) {
      // Debilitated planet found! Check classical cancellation conditions
      const conditions: string[] = [];
      let score = 0;

      const dispositorName = SIGN_LORDS[debSign];
      const dispositor = getP(dispositorName);

      // Condition 1: Dispositor of debilitated planet is in a Kendra (1, 4, 7, 10) from Lagna or Moon
      if (dispositor) {
        if ([1, 4, 7, 10].includes(dispositor.house)) {
          conditions.push(`Dispositor ${dispositorName} is in Kendra (House ${dispositor.house}) from Lagna`);
          score++;
        }
        if (moonP && [1, 4, 7, 10].includes(((dispositor.signIndex - moonP.signIndex + 12) % 12) + 1)) {
          conditions.push(`Dispositor ${dispositorName} is in Kendra from Moon`);
          score++;
        }
      }

      // Condition 2: Planet that gets exalted in this sign is in a Kendra
      const exaltedInThisSignPlanetName = Object.keys(EXALTATION_SIGNS).find(k => EXALTATION_SIGNS[k] === debSign);
      if (exaltedInThisSignPlanetName) {
        const exPlanet = getP(exaltedInThisSignPlanetName);
        if (exPlanet && [1, 4, 7, 10].includes(exPlanet.house)) {
          conditions.push(`Exaltation ruler ${exaltedInThisSignPlanetName} is in Kendra (House ${exPlanet.house})`);
          score++;
        }
      }

      // Condition 3: Planet in Navamsha is exalted or in own sign
      const d9P = getD9P(pName);
      if (d9P) {
        if (d9P.sign === EXALTATION_SIGNS[pName]) {
          conditions.push(`${pName} achieves Exaltation in Navamsha D9 (${d9P.sign})`);
          score += 2;
        } else if (SIGN_LORDS[d9P.sign]?.toLowerCase() === pName.toLowerCase()) {
          conditions.push(`${pName} is in Own Sign in Navamsha D9 (${d9P.sign})`);
          score += 1;
        }
      }

      const isCancelled = score >= 1;
      neechaBhanga.push({
        planet: pName,
        debilitatedSign: debSign,
        house: pData.house,
        isCancelled,
        score: Math.min(score, 4),
        conditionsMet: conditions,
        verdictEn: isCancelled
          ? `Neecha Bhanga Raja Yoga Formed (Score: ${score}/4). Debility is cancelled; hardship will convert into enduring mastery and high rank.`
          : `Uncancelled Debility. The planet requires active remedial strengthening through charity, devotion, and mantra.`,
        verdictHi: isCancelled
          ? `नीच भंग राजयोग घटित (अंक: ${score}/4)। नीचता भंग हो गई है; प्रारंभिक संघर्ष के उपरांत जातक को विशेष प्रतिष्ठा प्राप्त होगी।`
          : `नीचता यथावत है। इस ग्रह के शांति और जप-अनुष्ठान द्वारा अनुकूलता प्राप्त करनी चाहिए।`
      });
    }
  });

  // --------------------------------------------------------------------------
  // 6. JAIMINI CHARA KARAKAS & KARAKAMSHA
  // --------------------------------------------------------------------------
  // 7 Planets sorted by degree descending
  const sevenPlanets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn']
    .map(name => getP(name))
    .filter((p): p is PlanetPosition => Boolean(p))
    .sort((a, b) => b.degreeInSign - a.degreeInSign);

  const atmakarakaPlanet = sevenPlanets[0]?.name || 'Sun';
  const amatyakarakaPlanet = sevenPlanets[1]?.name || 'Jupiter';
  const bhratrikarakaPlanet = sevenPlanets[2]?.name || 'Mars';
  const matrikarakaPlanet = sevenPlanets[3]?.name || 'Moon';
  const putrakarakaPlanet = sevenPlanets[4]?.name || 'Mercury';
  const gnatikarakaPlanet = sevenPlanets[5]?.name || 'Venus';
  const darakarakaPlanet = sevenPlanets[6]?.name || 'Saturn';

  // Find Karakamsha (sign of Atmakaraka in Navamsha D9)
  const akD9 = getD9P(atmakarakaPlanet);
  const karakamshaSign: ZodiacSign = akD9?.sign || 'Aries';
  const karakamshaSignIdx = ZODIAC_SIGNS_LIST.indexOf(karakamshaSign);
  const karakamshaHouse = ((karakamshaSignIdx - lagnaSignIdx + 12) % 12) + 1;

  const jaiminiKarakas: JaiminiKarakas = {
    atmakaraka: {
      planet: atmakarakaPlanet,
      degree: sevenPlanets[0]?.degreeInSign || 0,
      sign: sevenPlanets[0]?.sign || 'Aries',
      meaning: 'Soul King (Atma): Core life lessons, spiritual direction, and divine destiny.'
    },
    amatyakaraka: {
      planet: amatyakarakaPlanet,
      degree: sevenPlanets[1]?.degreeInSign || 0,
      sign: sevenPlanets[1]?.sign || 'Aries',
      meaning: 'Minister (AmK): Career, livelihood, professional intellect, and worldly execution.'
    },
    bhratrikaraka: {
      planet: bhratrikarakaPlanet,
      degree: sevenPlanets[2]?.degreeInSign || 0,
      sign: sevenPlanets[2]?.sign || 'Aries',
      meaning: 'Guru / Mentor (BK): Spiritual teacher, guides, fatherly wisdom, and courageous kin.'
    },
    matrikaraka: {
      planet: matrikarakaPlanet,
      degree: sevenPlanets[3]?.degreeInSign || 0,
      sign: sevenPlanets[3]?.sign || 'Aries',
      meaning: 'Mother / Sanctuary (MK): Emotional shelter, inner happiness, real estate, and peace.'
    },
    putrakaraka: {
      planet: putrakarakaPlanet,
      degree: sevenPlanets[4]?.degreeInSign || 0,
      sign: sevenPlanets[4]?.sign || 'Aries',
      meaning: 'Creative Intellect (PK): Children, disciples, past-life merit (Purva Punya).'
    },
    gnatikaraka: {
      planet: gnatikarakaPlanet,
      degree: sevenPlanets[5]?.degreeInSign || 0,
      sign: sevenPlanets[5]?.sign || 'Aries',
      meaning: 'Challenger (GK): Obstacles, rivals, physical friction, and endurance lessons.'
    },
    darakaraka: {
      planet: darakarakaPlanet,
      degree: sevenPlanets[6]?.degreeInSign || 0,
      sign: sevenPlanets[6]?.sign || 'Aries',
      meaning: 'Spouse / Partner (DK): Marital union, key business allies, and sensual bonding.'
    },
    karakamshaSign,
    karakamshaHouse,
    spiritualFocusEn: `Atmakaraka ${atmakarakaPlanet} sits in ${karakamshaSign} in Navamsha (House ${karakamshaHouse} from Lagna). Reveals that the soul's highest spiritual path involves ${karakamshaSign} themes (${SIGN_ELEMENTS[karakamshaSign].en} path).`,
    spiritualFocusHi: `आत्मकारक ${atmakarakaPlanet} नवांश में ${karakamshaSign} (लग्न से ${karakamshaHouse}वां भाव) में स्थित है। यह जातक के आत्मिक उद्देश्य और जीवन की मूल साधना का द्योतक है।`
  };

  // --------------------------------------------------------------------------
  // 7. KARTARI STATUS (Hemming on Lagna, Moon, 7th House)
  // --------------------------------------------------------------------------
  const checkKartari = (targetName: string, targetHouseNum: number): KartariStatus => {
    const twelfthHouseNum = targetHouseNum === 1 ? 12 : targetHouseNum - 1;
    const secondHouseNum = targetHouseNum === 12 ? 1 : targetHouseNum + 1;

    const twelfthOccupants = d1Planets.filter(p => p.house === twelfthHouseNum).map(p => p.name);
    const secondOccupants = d1Planets.filter(p => p.house === secondHouseNum).map(p => p.name);

    const hasMalefics12 = twelfthOccupants.some(p => naturalMalefics.includes(p.toLowerCase()));
    const hasMalefics2 = secondOccupants.some(p => naturalMalefics.includes(p.toLowerCase()));

    const hasBenefics12 = twelfthOccupants.some(p => naturalBenefics.includes(p.toLowerCase()));
    const hasBenefics2 = secondOccupants.some(p => naturalBenefics.includes(p.toLowerCase()));

    if (hasMalefics12 && hasMalefics2 && !hasBenefics12 && !hasBenefics2) {
      return {
        target: targetName,
        type: 'papa_kartari',
        twelfthPlanets: twelfthOccupants,
        secondPlanets: secondOccupants,
        verdictEn: `Papa Kartari (Chokehold): Hemmed between malefics in Houses ${twelfthHouseNum} & ${secondHouseNum}. Creates persistent friction, feeling boxed in or suppressed.`,
        verdictHi: `पाप कर्तरी योग: भाव ${twelfthHouseNum} व ${secondHouseNum} में पाप ग्रहों से घिरा हुआ। रुकावटों और मानसिक दबाव का अनुभव कराता है।`
      };
    } else if (hasBenefics12 && hasBenefics2) {
      return {
        target: targetName,
        type: 'shubha_kartari',
        twelfthPlanets: twelfthOccupants,
        secondPlanets: secondOccupants,
        verdictEn: `Shubha Kartari (Sanctuary): Flanked by gentle benefics in Houses ${twelfthHouseNum} & ${secondHouseNum}. Grants unexpected divine rescues and continuous grace.`,
        verdictHi: `शुभ कर्तरी योग: दोनों ओर शुभ ग्रहों की छत्रछाया। जातक को दैवीय सुरक्षा और समय पर सहायता प्राप्त होती है।`
      };
    } else {
      return {
        target: targetName,
        type: 'neutral',
        twelfthPlanets: twelfthOccupants,
        secondPlanets: secondOccupants,
        verdictEn: `Balanced / Open boundaries. No constricting scissor-effect.`,
        verdictHi: `संतुलित स्थिति, कोई कर्तरी बंधन नहीं।`
      };
    }
  };

  const kartari: KartariStatus[] = [
    checkKartari('Ascendant (Lagna - Physical Body & Vitality)', 1),
    checkKartari('Chandra Lagna (Moon - Mental Peace)', moonP?.house || 1),
    checkKartari('7th House (Marriage & Public Contracts)', 7)
  ];

  // --------------------------------------------------------------------------
  // 8. D1 TO D9 DIGNITY SHIFT MATRIX (D1 promises, D9 delivers)
  // --------------------------------------------------------------------------
  const d1toD9Shifts: D1toD9ShiftItem[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'].map(pName => {
    const d1P = getP(pName);
    const d9P = getD9P(pName);

    const d1Sign = d1P?.sign || 'Aries';
    const d1House = d1P?.house || 1;
    const d1Dignity = d1P?.dignity || 'Neutral';

    const d9Sign = d9P?.sign || d1Sign;
    const d9House = d9P?.house || 1;
    const d9Dignity = d9P?.dignity || 'Neutral';

    const isVargottama = d1Sign === d9Sign;
    const isExToDeb = d1Dignity.toLowerCase().includes('exalt') && d9Dignity.toLowerCase().includes('debilitat');
    const isDebToEx = d1Dignity.toLowerCase().includes('debilitat') && d9Dignity.toLowerCase().includes('exalt');

    let shiftVerdict: D1toD9ShiftItem['shiftVerdict'] = 'steady';
    let interpretationEn = '';
    let interpretationHi = '';

    if (isVargottama) {
      shiftVerdict = 'vargottama';
      interpretationEn = `Vargottama (D1 ${d1Sign} = D9 ${d9Sign}). Deep planetary coherence; solid foundational strength that remains unfazed by outer turmoil.`;
      interpretationHi = `वर्गोत्तम (D1 व D9 दोनों में ${d1Sign})। अत्यंत दृढ़ और स्थायी शक्ति जो जीवनपर्यंत फल देती है।`;
      auspiciousMarks.push({
        planet: pName,
        type: 'vargottama',
        titleEn: `${pName} is Vargottama (${d1Sign})`,
        titleHi: `${pName} वर्गोत्तम है (${d1Sign})`,
        descriptionEn: `Occupies the same sign in D1 and D9. Grants extraordinary stability and unwavering resilience.`,
        descriptionHi: `D1 व D9 दोनों में समान राशि में होने से ग्रह को स्वराशि तुल्य बल प्राप्त होता है।`
      });
    } else if (isExToDeb) {
      shiftVerdict = 'exalted_to_debilitated';
      interpretationEn = `False Promise: Exalted in D1 (${d1Sign}) but Debilitated in D9 (${d9Sign}). Initial high hopes or glamour, but struggles to deliver consistent long-term results.`;
      interpretationHi = `छलकारी फल: D1 में उच्च किंतु D9 में नीच। प्रारंभिक उत्साह के बाद अपेक्षित परिणाम में कमी।`;
      vulnerabilities.push({
        planet: pName,
        type: 'sandhi',
        severity: 'high',
        titleEn: `${pName}: Exalted in D1 ➔ Debilitated in D9`,
        titleHi: `${pName}: D1 में उच्च ➔ D9 में नीच`,
        descriptionEn: `Planetary strength drops sharply in the Navamsha. Requires ground-level discipline rather than complacency.`,
        descriptionHi: `नवांश में नीच होने से ग्रह की आंतरिक शक्ति क्षीण होती है। अहंकार से बचना चाहिए।`
      });
    } else if (isDebToEx) {
      shiftVerdict = 'debilitated_to_exalted';
      interpretationEn = `Late Bloomer / Redemption: Debilitated in D1 (${d1Sign}) but Exalted in D9 (${d9Sign}). Starts with hardship, but transforms into astonishing mastery and triumph post-30s.`;
      interpretationHi = `विलंबित उत्थान: D1 में नीच किंतु D9 में उच्च। प्रारंभिक संघर्ष के पश्चात जातक असाधारण सफलता पाता है।`;
    } else if (d9Dignity.toLowerCase().includes('exalt') || d9Dignity.toLowerCase().includes('own')) {
      shiftVerdict = 'improved';
      interpretationEn = `Strengthened in D9 (${d9Sign}). Becomes substantially more productive as life progresses.`;
      interpretationHi = `नवांश में बल वृद्धि (${d9Sign})। आयु बढ़ने के साथ फल में उत्कृष्टता आती है।`;
    } else if (d9Dignity.toLowerCase().includes('debilitat') || d9Dignity.toLowerCase().includes('enemy')) {
      shiftVerdict = 'weakened';
      interpretationEn = `Subconscious friction in D9 (${d9Sign}). Demands conscious effort to preserve its virtues.`;
      interpretationHi = `नवांश में शिथिलता (${d9Sign})। सतत प्रयास की आवश्यकता।`;
    } else {
      shiftVerdict = 'steady';
      interpretationEn = `Steady continuity between D1 (${d1Sign}) and D9 (${d9Sign}).`;
      interpretationHi = `सामान्य निरंतरता (D1: ${d1Sign}, D9: ${d9Sign})।`;
    }

    return {
      planet: pName,
      d1Sign,
      d1House,
      d1Dignity,
      d9Sign,
      d9House,
      d9Dignity,
      shiftVerdict,
      interpretationEn,
      interpretationHi
    };
  });

  // --------------------------------------------------------------------------
  // 9. SPECIAL PROSPERITY LAGNAS (Indu Lagna & Upapada Lagna)
  // --------------------------------------------------------------------------
  // Indu Lagna:
  // 9th house from Lagna:
  const ninthFromLagnaSign = ZODIAC_SIGNS_LIST[(lagnaSignIdx + 8) % 12];
  const ninthFromLagnaLord = SIGN_LORDS[ninthFromLagnaSign];
  const raysLagna = PLANET_KALA_RAYS[ninthFromLagnaLord] || 10;

  // 9th house from Moon:
  const moonSignIdx = ZODIAC_SIGNS_LIST.indexOf(moonSign);
  const ninthFromMoonSign = ZODIAC_SIGNS_LIST[(moonSignIdx + 8) % 12];
  const ninthFromMoonLord = SIGN_LORDS[ninthFromMoonSign];
  const raysMoon = PLANET_KALA_RAYS[ninthFromMoonLord] || 10;

  const totalRays = raysLagna + raysMoon;
  let remainder = totalRays % 12;
  if (remainder === 0) remainder = 12;

  // Count remainder from Moon
  const induLagnaSignIdx = (moonSignIdx + remainder - 1) % 12;
  const induLagnaSign = ZODIAC_SIGNS_LIST[induLagnaSignIdx];
  const induLagnaHouseFromLagna = ((induLagnaSignIdx - lagnaSignIdx + 12) % 12) + 1;

  const induOccupants = d1Planets.filter(p => p.sign === induLagnaSign).map(p => p.name);
  const induBenefics = induOccupants.filter(p => naturalBenefics.includes(p.toLowerCase()));
  const induMalefics = induOccupants.filter(p => naturalMalefics.includes(p.toLowerCase()));

  let wealthCeilingScore = 6;
  if (induBenefics.length > 0) wealthCeilingScore += induBenefics.length * 2;
  if (induMalefics.length > 0) wealthCeilingScore -= induMalefics.length;
  wealthCeilingScore = Math.max(1, Math.min(10, wealthCeilingScore));

  const induLagna = {
    sign: induLagnaSign,
    houseFromLagna: induLagnaHouseFromLagna,
    occupants: induOccupants,
    aspectedBy: [] as string[],
    wealthCeilingScore,
    verdictEn: `Indu Lagna falls in ${induLagnaSign} (House ${induLagnaHouseFromLagna} from Ascendant). Rays: 9th lord from Lagna (${ninthFromLagnaLord}=${raysLagna}) + 9th lord from Moon (${ninthFromMoonLord}=${raysMoon}) = ${totalRays} (Modulo remainder ${remainder}). ${induOccupants.length > 0 ? `Occupied by ${induOccupants.join(', ')}.` : 'Vacant, driven by its lord ' + SIGN_LORDS[induLagnaSign] + '.'}`,
    verdictHi: `इन्दु लग्न ${induLagnaSign} में (लग्न से ${induLagnaHouseFromLagna}वां भाव) है। नवमेश किरण योग = ${totalRays}। यह जातक की धन-संपत्ति और आर्थिक क्षमता का मुख्य आधार है।`
  };

  // Upapada Lagna (UL):
  // 12th house from Lagna:
  const twelfthSign = ZODIAC_SIGNS_LIST[(lagnaSignIdx + 11) % 12];
  const twelfthLord = SIGN_LORDS[twelfthSign];
  const twelfthLordPlanet = getP(twelfthLord);
  const twelfthLordSign = twelfthLordPlanet?.sign || twelfthSign;
  const twelfthLordSignIdx = ZODIAC_SIGNS_LIST.indexOf(twelfthLordSign);

  // Distance from 12th house to 12th lord:
  const dist12 = (twelfthLordSignIdx - ((lagnaSignIdx + 11) % 12) + 12) % 12;
  let ulSignIdx = (((lagnaSignIdx + 11) % 12) + dist12) % 12;
  // Parashari exception: if UL falls in 12th or 6th from itself, move 10th
  if (dist12 === 0 || dist12 === 6) {
    ulSignIdx = (ulSignIdx + 9) % 12;
  }
  const ulSign = ZODIAC_SIGNS_LIST[ulSignIdx];
  const ulHouseFromLagna = ((ulSignIdx - lagnaSignIdx + 12) % 12) + 1;

  const upapadaLagna = {
    sign: ulSign,
    houseFromLagna: ulHouseFromLagna,
    verdictEn: `Upapada Lagna (UL) sits in ${ulSign} (House ${ulHouseFromLagna}). Represents the true karmic partner, marital stability, and longevity of matrimony.`,
    verdictHi: `उपपद लग्न (UL) ${ulSign} में (लग्न से ${ulHouseFromLagna}वां भाव) स्थित है। यह वैवाहिक स्थायित्व और जीवनसाथी के स्वभाव का सूचक है।`
  };

  // --------------------------------------------------------------------------
  // 10. DYNAMIC TIMING: DOUBLE TRANSIT (Jupiter + Saturn Simultaneous Trigger)
  // --------------------------------------------------------------------------
  // Determine Saturn & Jupiter current transit signs dynamically:
  let transitSaturnSign: ZodiacSign | undefined = kundali.gochar?.items?.find((p) => p.planet === 'Saturn')?.currentSign
    || (kundali.charts as any)?.Transit?.planets?.find((p: any) => p.name === 'Saturn')?.sign;
  let transitJupiterSign: ZodiacSign | undefined = kundali.gochar?.items?.find((p) => p.planet === 'Jupiter')?.currentSign
    || (kundali.charts as any)?.Transit?.planets?.find((p: any) => p.name === 'Jupiter')?.sign;

  if (!transitSaturnSign || !transitJupiterSign) {
    const liveEphem = calculateLivePlanetaryEphemeris(new Date());
    if (!transitSaturnSign) transitSaturnSign = liveEphem.find(p => p.planet === 'Saturn')?.sign || 'Pisces';
    if (!transitJupiterSign) transitJupiterSign = liveEphem.find(p => p.planet === 'Jupiter')?.sign || 'Taurus';
  }

  const satSignIdx = ZODIAC_SIGNS_LIST.indexOf(transitSaturnSign);
  const jupSignIdx = ZODIAC_SIGNS_LIST.indexOf(transitJupiterSign);

  // Saturn influences: 1st (occupies), 3rd, 7th, 10th
  const saturnInfluencedSigns = [
    transitSaturnSign,
    ZODIAC_SIGNS_LIST[(satSignIdx + 2) % 12], // 3rd
    ZODIAC_SIGNS_LIST[(satSignIdx + 6) % 12], // 7th
    ZODIAC_SIGNS_LIST[(satSignIdx + 9) % 12]  // 10th
  ];

  // Jupiter influences: 1st (occupies), 5th, 7th, 9th
  const jupiterInfluencedSigns = [
    transitJupiterSign,
    ZODIAC_SIGNS_LIST[(jupSignIdx + 4) % 12], // 5th
    ZODIAC_SIGNS_LIST[(jupSignIdx + 6) % 12], // 7th
    ZODIAC_SIGNS_LIST[(jupSignIdx + 8) % 12]  // 9th
  ];

  const HOUSE_MEANINGS: Record<number, { en: string; hi: string; domain: string }> = {
    1: { en: 'Self, Health, Vitality, New Life Chapter', hi: 'तनु भाव: स्वास्थ्य, आत्म-विकास, नया अध्याय', domain: 'Personal Transformation' },
    2: { en: 'Family Wealth, Liquid Assets, Speech', hi: 'धन भाव: पारिवारिक संपत्ति, वाणी, संचित कोष', domain: 'Wealth & Assets' },
    3: { en: 'Initiative, Courage, Siblings, Short Travels', hi: 'सहज भाव: पराक्रम, भाई-बहन, लघु यात्राएं', domain: 'Enterprise & Siblings' },
    4: { en: 'Mother, Home, Real Estate, Vehicles', hi: 'सुख भाव: माता, गृह, भूमि-भवन, वाहन सुख', domain: 'Real Estate & Home' },
    5: { en: 'Children, Creative Intelligence, Speculation', hi: 'सुत भाव: संतान, बुद्धि, रचनात्मकता, पूर्व पुण्य', domain: 'Children & Genius' },
    6: { en: 'Debts, Diseases, Legal Battles, Service', hi: 'रिपु भाव: रोग, ऋण, प्रतियोगिता, सेवा', domain: 'Service & Litigation' },
    7: { en: 'Marriage, Spouse, Business Partnerships', hi: 'जाया भाव: विवाह, जीवनसाथी, व्यावसायिक अनुबंध', domain: 'Marriage & Partnerships' },
    8: { en: 'Longevity, Sudden Transitions, Inheritance', hi: 'आयु भाव: अचानक परिवर्तन, गुप्त विद्या, पैतृक लाभ', domain: 'Occult & Transformation' },
    9: { en: 'Dharma, Father, Long Journeys, Guru Blessing', hi: 'धर्म भाव: भाग्य, पिता, तीर्थाटन, उच्च विद्या', domain: 'Fortune & Pilgrimage' },
    10: { en: 'Career, Rank, Authority, Public Standing', hi: 'कर्म भाव: आजीविका, पद-प्रतिष्ठा, राज्य सम्मान', domain: 'Career & Authority' },
    11: { en: 'Gains, Ambition Fulfillment, Elder Siblings', hi: 'लाभ भाव: मनोकामना पूर्ति, आय वृद्धि, मित्र वर्ग', domain: 'Major Gains & Social Circle' },
    12: { en: 'Expenditure, Foreign Settlement, Spiritual Moksha', hi: 'व्यय भाव: विदेश वास, अस्पताल, मोक्ष, दान', domain: 'Foreign Travel & Liberation' }
  };

  const doubleTransitRadar: DoubleTransitItem[] = [];

  for (let h = 1; h <= 12; h++) {
    const signOfHouse = ZODIAC_SIGNS_LIST[(lagnaSignIdx + h - 1) % 12];
    const isSatInfluenced = saturnInfluencedSigns.includes(signOfHouse);
    const isJupInfluenced = jupiterInfluencedSigns.includes(signOfHouse);
    const isSimultaneouslyActivated = isSatInfluenced && isJupInfluenced;

    let satInfluence = 'none';
    if (signOfHouse === transitSaturnSign) satInfluence = 'occupies';
    else if (signOfHouse === ZODIAC_SIGNS_LIST[(satSignIdx + 2) % 12]) satInfluence = 'aspects_3';
    else if (signOfHouse === ZODIAC_SIGNS_LIST[(satSignIdx + 6) % 12]) satInfluence = 'aspects_7';
    else if (signOfHouse === ZODIAC_SIGNS_LIST[(satSignIdx + 9) % 12]) satInfluence = 'aspects_10';

    let jupInfluence = 'none';
    if (signOfHouse === transitJupiterSign) jupInfluence = 'occupies';
    else if (signOfHouse === ZODIAC_SIGNS_LIST[(jupSignIdx + 4) % 12]) jupInfluence = 'aspects_5';
    else if (signOfHouse === ZODIAC_SIGNS_LIST[(jupSignIdx + 6) % 12]) jupInfluence = 'aspects_7';
    else if (signOfHouse === ZODIAC_SIGNS_LIST[(jupSignIdx + 8) % 12]) jupInfluence = 'aspects_9';

    const info = HOUSE_MEANINGS[h];

    doubleTransitRadar.push({
      houseNumber: h,
      sign: signOfHouse,
      significanceEn: info.en,
      significanceHi: info.hi,
      saturnInfluence: satInfluence,
      jupiterInfluence: jupInfluence,
      isSimultaneouslyActivated,
      activatedLifeDomain: info.domain,
      practicalTimingVerdictEn: isSimultaneouslyActivated
        ? `HIGH EVENT WINDOW: Both Saturn (taskmaster) and Jupiter (sanctifier) are jointly energizing House ${h} (${signOfHouse}). Events regarding ${info.domain} are mature for manifestation right now.`
        : `Dormant / Single-planet aspect. No combined double transit convergence.`,
      practicalTimingVerdictHi: isSimultaneouslyActivated
        ? `उच्च फल प्राप्ति काल: शनि एवं देवगुरु बृहस्पति दोनों संयुक्त रूप से ${h}वें भाव (${signOfHouse}) को दृष्टि/स्थिति प्रदान कर रहे हैं। ${info.domain} से संबंधित महत्वपूर्ण घटना शीघ्र घटित हो सकती है।`
        : `एकल प्रभाव, कोई संयुक्त युति दृष्टि नहीं।`
    });
  }

  // Active Dasha info
  const curMaha = kundali.dashas?.find(d => d.isCurrent) || kundali.dashas?.[0];
  const curAntar = curMaha?.antardashas?.find(a => a.isCurrent) || curMaha?.antardashas?.[0];

  const currentMahaName = curMaha?.planet || 'Jupiter';
  const currentAntarName = curAntar?.planet || 'Saturn';

  // Check if current dasha lords are connected to double-transit houses
  const activeDoubleHouses = doubleTransitRadar.filter(d => d.isSimultaneouslyActivated).map(d => d.houseNumber);
  const mahaLordP = getP(currentMahaName);
  const antarLordP = getP(currentAntarName);

  const mahaHouse = mahaLordP?.house || 1;
  const antarHouse = antarLordP?.house || 1;

  const isDashaAligned = activeDoubleHouses.includes(mahaHouse) || activeDoubleHouses.includes(antarHouse);

  // --------------------------------------------------------------------------
  // 11. KARMA DIAGNOSTIC (Dridha vs. Adridha)
  // --------------------------------------------------------------------------
  let dridhaPoints = 0;
  if (vulnerabilities.filter(v => v.severity === 'critical').length > 0) dridhaPoints += 2;
  if (vulnerabilities.filter(v => v.severity === 'high').length >= 2) dridhaPoints += 2;
  if (neechaBhanga.some(n => !n.isCancelled)) dridhaPoints += 2;
  if (kartari.some(k => k.type === 'papa_kartari')) dridhaPoints += 1;

  let karmaType: 'dridha' | 'dridha_adridha' | 'adridha' = 'dridha_adridha';
  let karmaTitleEn = 'Dridha-Adridha Karma (Flexible-Fixed Destiny)';
  let karmaTitleHi = 'दृढ़-अदृढ़ कर्म (मध्यम कर्म - यत्न साध्य)';
  let karmaPrescriptionEn = 'A balanced blend of fixed lessons and negotiable results. Responds remarkably well to discipline, Vedic mantras, charitable acts, and gemstones.';
  let karmaPrescriptionHi = 'प्रयत्न और वैदिक उपायों से बदलने योग्य। दान, जप-अनुष्ठान और उपयुक्त रत्नों से अनुकूल परिणाम प्राप्त होते हैं।';

  if (dridhaPoints >= 4) {
    karmaType = 'dridha';
    karmaTitleEn = 'Dridha Karma (Fixed Destiny / Prarabdha)';
    karmaTitleHi = 'दृढ़ कर्म (अटल प्रारब्ध)';
    karmaPrescriptionEn = 'Deep, hardened karmas that must be experienced and exhausted with patience, equanimity, and spiritual surrender (Sadhana / Tarpana / Seva).';
    karmaPrescriptionHi = 'यह पूर्वजन्मों का अटल प्रारब्ध है। इसे धैर्य, ईश्वरीय शरणागति और निष्काम सेवा द्वारा शांत किया जा सकता है।';
  } else if (dridhaPoints <= 1) {
    karmaType = 'adridha';
    karmaTitleEn = 'Adridha Karma (Fluid Destiny)';
    karmaTitleHi = 'अदृढ़ कर्म (लचीला प्रारब्ध)';
    karmaPrescriptionEn = 'Light karmic debt. Any conscious positive lifestyle adjustment, focused goal setting, or simple daily prayer will yield immediate breakthrough.';
    karmaPrescriptionHi = 'हल्के कर्म बंधन। थोड़े से संकल्प, सदाचार और सामान्य पूजा से ही शीघ्र सफलता व शांति मिलती है।';
  }

  // --------------------------------------------------------------------------
  // 12. CONSULTATION BRIEF (Executive Summary for Astrologer)
  // --------------------------------------------------------------------------
  const activeDoubleDomains = doubleTransitRadar
    .filter(d => d.isSimultaneouslyActivated)
    .map(d => d.activatedLifeDomain)
    .join(', ');

  const consultationBrief = {
    first60SecondsTakeawayEn: `Native is ${lagnaSign} Ascendant with ${moonSign} Moon (${paksha}). The foundational pillar strength is ${pillarStrength.toUpperCase()} with ${kendraBenefics} Kendra benefics. Active life focus is currently channeled into: ${activeDoubleDomains || 'Self-realization and structural career work'}. Current Dasha: ${currentMahaName}-${currentAntarName}.`,
    first60SecondsTakeawayHi: `जातक का लग्न ${lagnaSign} और चंद्र राशि ${moonSign} है। कुंडली का मूल आधार ${pillarStrength === 'exceptional' ? 'अति श्रेष्ठ' : pillarStrength === 'solid' ? 'सुदृढ़' : 'मध्यम'} है। वर्तमान में सक्रिय गोचर व दशा का मुख्य केंद्र: ${activeDoubleDomains || 'आत्म-विकास व कार्यक्षेत्र'}। वर्तमान दशा: ${currentMahaName}-${currentAntarName}।`,
    criticalWarningEn: vulnerabilities.length > 0
      ? `${vulnerabilities[0].titleEn}: ${vulnerabilities[0].descriptionEn}`
      : undefined,
    criticalWarningHi: vulnerabilities.length > 0
      ? `${vulnerabilities[0].titleHi}: ${vulnerabilities[0].descriptionHi}`
      : undefined,
    primaryBlessingEn: auspiciousMarks.length > 0
      ? `${auspiciousMarks[0].titleEn}: ${auspiciousMarks[0].descriptionEn}`
      : `Lagna Lord ${lagnaLord} in House ${getP(lagnaLord)?.house || 1} gives natural resilience.`,
    primaryBlessingHi: auspiciousMarks.length > 0
      ? `${auspiciousMarks[0].titleHi}: ${auspiciousMarks[0].descriptionHi}`
      : `लग्नेश ${lagnaLord} भाव ${getP(lagnaLord)?.house || 1} में जातक को नैसर्गिक आत्मबल प्रदान करता है।`
  };

  return {
    timestamp: new Date().toISOString(),
    threePillars,
    functionalClassification,
    kendraTrikonaScore: {
      kendraBenefics,
      kendraMalefics,
      trikonaBenefics,
      dusthanaAfflictionScore: dusthanaAfflictions,
      overallPillarStrength: pillarStrength,
      summaryEn: `${kendraBenefics} benefics in Vishnu houses (1,4,7,10); ${trikonaBenefics} in Lakshmi houses (5,9); ${dusthanaAfflictions} malefics in dusthanas (6,8,12).`,
      summaryHi: `केंद्र (विष्णु स्थान) में ${kendraBenefics} शुभ ग्रह; त्रिकोण (लक्ष्मी स्थान) में ${trikonaBenefics} शुभ ग्रह; दुष्ट स्थान में ${dusthanaAfflictions} पापी प्रभाव।`
    },
    vulnerabilities,
    auspiciousMarks,
    neechaBhanga,
    jaiminiKarakas,
    kartari,
    d1toD9Shifts,
    specialLagnas: {
      induLagna,
      upapadaLagna
    },
    doubleTransitRadar,
    activeDashaResonance: {
      mahadasha: currentMahaName,
      antardasha: currentAntarName,
      alignmentWithDoubleTransit: isDashaAligned,
      operationalAdviceEn: isDashaAligned
        ? `Resonance Confirmed: Mahadasha lord ${currentMahaName} directly governs or occupies the house simultaneously energized by Saturn & Jupiter. Major breakthrough window!`
        : `Independent Action: Dasha lord operates in House ${mahaHouse}, while transits activate Houses ${activeDoubleHouses.join(', ')}. Multiple concurrent life themes in play.`,
      operationalAdviceHi: isDashaAligned
        ? `दशा व गोचर की पूर्ण संगति: महादशा स्वामी ${currentMahaName} उसी भाव से संबंधित है जिसे शनि व गुरु संयुक्त रूप से सक्रिय कर रहे हैं। जीवन में बड़े परिवर्तन का समय!`
        : `दशा स्वामी भाव ${mahaHouse} में कार्यरत है, जबकि संयुक्त गोचर भाव ${activeDoubleHouses.join(', ')} को सक्रिय कर रहा है। बहुआयामी जीवन गतिविधियां जारी रहेंगी।`
    },
    karmaDiagnostic: {
      type: karmaType,
      titleEn: karmaTitleEn,
      titleHi: karmaTitleHi,
      prescriptionEn: karmaPrescriptionEn,
      prescriptionHi: karmaPrescriptionHi
    },
    consultationBrief
  };
}
